import { Typography } from '@material-ui/core';
import { InvestidorResponse, GrupoAcesso } from 'common/genericTypes';
import { tipoInvestidorDescriptions } from 'common/genericDescriptions';
import { Section } from 'm2m.components';
import React from 'react';
import { RootState } from 'store';
import { addCEPMask, addCpfCnpjMask, addTelefoneMask } from 'utils/mask';
import { useDispatch, useSelector } from 'react-redux';
import { formatDate } from 'utils/date';
import { formatCurrencyNumber } from 'utils/number';
import { labelsPf, labelsPj } from 'pages/investidores/registro/informacoesPrincipais/labels';

interface Props {
  investidor: InvestidorResponse;
}

export const InvestidorInformacoesPrincipais: React.FC<Props> = (props) => {
  const { id, representantesLegais, documentos, ...informacoesPrincipais } = props.investidor;

  const labels = informacoesPrincipais.cpfCnpj.length > 11 ? labelsPj : labelsPf;

  const isUsuarioOperacoes = useSelector((state: RootState) => state.auth.usuario.grupoAcesso === GrupoAcesso.Operacoes);
  const isUsuarioCadastroOperacoes = useSelector((state: RootState) => state.auth.usuario.grupoAcesso === GrupoAcesso.CadastroOperacoes);

  return (
    <>
      <Section title={'Informações Principais'} defaultGrid>
        <div>
          <Typography variant="body2">{labels.cpfCnpj}</Typography>
          <Typography variant="body1">{addCpfCnpjMask(informacoesPrincipais.cpfCnpj)}</Typography>
        </div>
        <div>
          <Typography variant="body2">{labels.razaosocial}</Typography>
          <Typography variant="body1">{informacoesPrincipais.razaoSocial}</Typography>
        </div>
        <div>
          <Typography variant="body2">{labels.dataNascimento}</Typography>
          <Typography variant="body1">{formatDate(informacoesPrincipais.dataNascimento)}</Typography>
        </div>
        <div>
          <Typography variant="body2">{labels.nacionalidade}</Typography>
          <Typography variant="body1">{informacoesPrincipais.nacionalidade}</Typography>
        </div>
        <div>
          <Typography variant="body2">{labels.profissao}</Typography>
          <Typography variant="body1">{informacoesPrincipais.profissaoAtividadePrincipal}</Typography>
        </div>
        <div>
          <Typography variant="body2">{labels.tipoInvestidor}</Typography>
          <Typography variant="body1">{tipoInvestidorDescriptions[informacoesPrincipais.tipo]}</Typography>
        </div>
        <div>
          <Typography variant="body2">{labels.rendaMensalDeclarada}</Typography>
          <Typography variant="body1">{formatCurrencyNumber(informacoesPrincipais.rendaMensalDeclarada)}</Typography>
        </div>
        <div>
          <Typography variant="body2">{labels.patrimonioDeclarado}</Typography>
          <Typography variant="body1">{formatCurrencyNumber(informacoesPrincipais.patrimonioDeclarado)}</Typography>
        </div>
        <div>
          <Typography variant="body2">{labels.investidorPoliticamenteExposto}</Typography>
          <Typography variant="body1">
            {informacoesPrincipais.investidorPoliticamenteExposto ? 'Sim' : 'Não'}
          </Typography>
        </div>
        <div>
          <Typography variant="body2">{labels.vinculadoCustodiante}</Typography>
          <Typography variant="body1">{informacoesPrincipais.vinculadoCustodiante ? 'Sim' : 'Não'}</Typography>
        </div>

        {(isUsuarioOperacoes || isUsuarioCadastroOperacoes) && (
          <>
            <div>
              <Typography variant="body2">{labels.razaoSocialCustodiante}</Typography>
              <Typography variant="body1">{informacoesPrincipais.razaoSocialCustodiante}</Typography>
            </div>

            <div>
              <Typography variant="body2">{labels.cnpjCustodiante}</Typography>
              <Typography variant="body1">{addCpfCnpjMask(informacoesPrincipais.cnpjCustodiante)}</Typography>
            </div>
          </>
        )}

        {informacoesPrincipais.cpfCnpj.length > 11 && (
          <div>
            <Typography variant="body2">{labels.cnaePrincipal}</Typography>
            <Typography variant="body1">{informacoesPrincipais.cnaePrincipal || '-'}</Typography>
          </div>
        )}

      </Section>
      <Section title={labels.endereco} defaultGrid>
        <div>
          <Typography variant="body2">{'CEP:'}</Typography>
          <Typography variant="body1">{addCEPMask(informacoesPrincipais.cep)}</Typography>
        </div>
        <div>
          <Typography variant="body2">{'Logadouro:'}</Typography>
          <Typography variant="body1">{informacoesPrincipais.logradouro}</Typography>
        </div>
        <div>
          <Typography variant="body2">{'Número:'}</Typography>
          <Typography variant="body1">{informacoesPrincipais.numero}</Typography>
        </div>
        <div>
          <Typography variant="body2">{'Complemento:'}</Typography>
          <Typography variant="body1">{informacoesPrincipais.complemento}</Typography>
        </div>
        <div>
          <Typography variant="body2">{'Bairro:'}</Typography>
          <Typography variant="body1">{informacoesPrincipais.bairro}</Typography>
        </div>
        <div>
          <Typography variant="body2">{'Cidade:'}</Typography>
          <Typography variant="body1">{informacoesPrincipais.cidade}</Typography>
        </div>
        <div>
          <Typography variant="body2">{'Unidade da Federação:'}</Typography>
          <Typography variant="body1">{informacoesPrincipais.uf}</Typography>
        </div>
      </Section>
      <Section title={'Informações para Contato'} defaultGrid>
        <div>
          <Typography variant="body2">{'Telefones:'}</Typography>
          {informacoesPrincipais.telefones.map((telefone) => (
            <Typography variant="body1">{addTelefoneMask(telefone)}</Typography>
          ))}
        </div>
        <div>
          <Typography variant="body2">{'E-mail:'}</Typography>
          <Typography variant="body1">{informacoesPrincipais.email || ''}</Typography>
        </div>
      </Section>
      <Section title={'Informações Bancárias'} defaultGrid>
        <div>
          <Typography variant="body2">{'Banco:'}</Typography>
          <Typography variant="body1">{informacoesPrincipais.razaoSocialBanco || ''}</Typography>
        </div>
        <div>
          <Typography variant="body2">{'ISPB do Banco:'}</Typography>
          <Typography variant="body1">{informacoesPrincipais.ispbBanco || ''}</Typography>
        </div>
        <div>
          <Typography variant="body2">{'Agência:'}</Typography>
          <Typography variant="body1">{informacoesPrincipais.agenciaBanco || ''}{informacoesPrincipais.agenciaDigitoBanco ? `-${informacoesPrincipais.agenciaDigitoBanco}` : ''}</Typography>
        </div>
        <div>
          <Typography variant="body2">{'Conta:'}</Typography>
          <Typography variant="body1">{informacoesPrincipais.contaBanco || ''}{informacoesPrincipais.contaDigitoBanco ? `-${informacoesPrincipais.contaDigitoBanco}` : ''}</Typography>
        </div>
      </Section>
    </>
  );
};
